/**
 * What the party WITHOUT possession sees while the other side holds the document.
 *
 * ## What this replaces
 *
 * Previously the editor route simply bounced them to the deal page: a document
 * they are a party to, in a deal they belong to, became unreachable the moment
 * the other side took possession. There was no way to re-read the terms you had
 * just been sent — you had to ask the other party to send it again.
 *
 * ## What it shows
 *
 * The most recent VERSION SNAPSHOT, read-only, with a banner naming who holds
 * the document. Deliberately NOT the live body: the other party's in-progress
 * edits are theirs until they hand it back, and showing a moving document would
 * misrepresent what was actually agreed. A snapshot is a fixed, citable state —
 * the one taken when possession transferred.
 *
 * Earlier snapshots stay reachable through the same panel, so a party can look
 * back over the exchange rather than only at the latest handover.
 *
 * This is a UI affordance, not an access-control boundary — the server already
 * decides who may read this document at all (`DocumentViewPermissionValidatorService`).
 * It narrows what a permitted viewer is shown; it never widens it.
 */

import React, { useMemo } from 'react';
import { ReactComponent as InfoIcon } from '../../../../shared/icons/info-icon.svg';
import { DealVersionViewer } from './DealVersionViewer';
import type { CompareVersion } from './compareTypes';
import './awaitingCounterparty.scss';

export interface AwaitingCounterpartyViewProps {
  /** All versions, newest first, INCLUDING the synthetic "current" entry. */
  versions: CompareVersion[];
}

export function AwaitingCounterpartyView({ versions }: AwaitingCounterpartyViewProps) {
  // Drop the synthetic entries that resolve to the LIVE body. Those are exactly
  // what this view must not show: 'current' is the other party's working copy,
  // and 'pre-migration' is an internal artifact.
  const snapshots = useMemo(
    () => versions.filter((v) => v.key !== 'current' && v.key !== 'pre-migration'),
    [versions]
  );

  // Deliberately NOT the possession team's name. A counterparty team is created
  // as `CreateTeamService.new(name: deal.title)`, so `possessionTeam.name` is
  // the DEAL's title — the banner read "new docx editor tests has the document".
  // "Counterparty" is the role the reader actually cares about, and it is right
  // whatever the team ended up being called.

  if (snapshots.length === 0) {
    // No handover snapshot exists — nothing can be shown without exposing the
    // live document. Say so plainly instead of rendering an empty editor.
    return (
      <div className="awaiting-counterparty awaiting-counterparty--empty">
        <div className="awaiting-counterparty__banner" role="status">
          <InfoIcon className="awaiting-counterparty__icon" />
          <div className="awaiting-counterparty__text">
            <strong>Counterparty has the document.</strong>{' '}
            <span>
              There is no shared version to display yet. You will be able to open it again when they
              send the document back to your team.
            </span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="awaiting-counterparty">
      {/* Small info bar above the document, in the app's existing notice
          language (icon + tinted strip + accent rule) — see
          ExecutedDocumentInfoBar. It states the two facts the viewer needs:
          this is read-only, and it is the version THEY sent, not what the
          other side is working on right now. */}
      <div className="awaiting-counterparty__banner" role="status">
        <InfoIcon className="awaiting-counterparty__icon" />
        <div className="awaiting-counterparty__text">
          <strong>Counterparty has the document.</strong>{' '}
          <span>
            You're viewing the version you sent as read-only. Any changes they make are not shown
            until they send the document back to your team.
          </span>
        </div>
      </div>
      <div className="awaiting-counterparty__viewer">
        <DealVersionViewer versions={snapshots} />
      </div>
    </div>
  );
}

export default AwaitingCounterpartyView;
